<template>
    <div class="game" :class="classes">
        <dysfunctional v-if="!isFunctioning"></dysfunctional>
        <template v-else>
            <b-card class="mb-3 header w-100 card-1">
                <header-container :game-id="gameId"></header-container>
            </b-card>
            <template v-if="!isLogged" class = "card-2 w-100">
                <b-card class="mb-3" v-if="firstBanner" v-html="firstBanner"></b-card>
                <b-card class="mb-3" v-if="secondBanner" v-html="secondBanner"></b-card>
            </template>
            <b-card class="game-card mb-3 card-3 w-100" v-if="isLogged" body-class="p-0">
                <b-row>
                    <b-col lg="8" class="pl-3 pb-2 pt-2 pr-2 p-md-3">
                        <selection-container @purchase="purchase"
                            @statSymbolsSet="usingSymbols = []"
                            @choice="setChoice"
                            :using-symbols="usingSymbols"
                            :is-purchasing="isPurchasing"
                            :alertPayload="alertPayload">
                        </selection-container>
                    </b-col>
                    <b-col lg="4" class="pr-3 pl-3 pl-lg-0 pt-0 tickets-container">
                        <tickets-container :game-id="gameId"></tickets-container>
                        <b-card class="mt-3" v-if="fourthBanner" v-html="fourthBanner"></b-card>
                    </b-col>
                </b-row>
            </b-card>
            <b-card class="mb-3 card-4 w-100">
                <stats-container @useSymbols="useStatSymbols" :game-id="gameId"></stats-container>
            </b-card>
            <b-card class="mb-3 card-5 w-100" v-if="!isLogged && thirdBanner" v-html="thirdBanner"></b-card>
            <b-card class="mb-3 card-6 w-100">
                <multipliers-container :game-id="gameId"></multipliers-container>
            </b-card>

            <bottom-navigation class="card-7 w-100"></bottom-navigation>
        </template>
    </div>
</template>

<script>
import Axios from "axios";
import moment from 'moment';

import HeaderContainer from '@tv/components/partials/header/Container';
import SelectionContainer from "@tv/components/partials/purchase/Container";
import TicketsContainer from "@tv/components/partials/tickets/Container";
import StatsContainer from "@tv/components/partials/stats/Main";
import MultipliersContainer from "@tv/components/partials/multipliers/Container";
import Dysfunctional from "@tv/components/partials/Dysfunctional";
import { mapState, mapMutations } from 'vuex';
import { syncFrameHeight } from "@tv/helpers";
import lang from '@tv/filters/lang';
import BottomNavigation from '@tv/components/partials/BottomNavigation';

export default {
    props: ['gameId'],

    components: { HeaderContainer, SelectionContainer, TicketsContainer, StatsContainer, MultipliersContainer, Dysfunctional, BottomNavigation },

    data() {
        return {
            isPurchasing: false,
            usingSymbols: [],
            alertPayload: null,
            alertTimeout: null,

            choice: 'manual',
            activePage: 'statistic',
        };
    },

    computed: {
        ...mapState(['purchaseGameId', 'selection', 'channels', 'isLogged', 'selectionsOfGames', 'datetime']),

        banners() {
            if (!Config.banners) {
                return [];
            }

            return Config.banners.split("\n");
        },

        firstBanner() {
            return this.banners[0];
        },

        secondBanner() {
            return this.banners[1];
        },

        thirdBanner() {
            return this.banners[2];
        },

        fourthBanner() {
            return this.banners[3];
        },

        classes() {
            return [`game-${this.game.name}`, 'd-flex', 'flex-wrap'];
        },

        game() {
            return Config.games[this.gameId];
        },

        nextDrawsIds() {
            const channelId = Config.games[this.gameId].channel_id;
            const channel = this.channels[channelId];

            if (channel) {
                return channel.draws.next_list.slice(0, this.selection.continueCount).map(draw => draw._id);
            }
        },

        isFunctioning() {
            const channelId = this.game.channel_id;
            const channel = this.channels[channelId];

            try {
                const endTime = moment(channel.draws.next.datetime).add(this.game.studio_max_duration, 'seconds');
    
                return moment(this.datetime).isBefore(endTime);                
            } catch (e) {
                return false;
            }

        },
    },

    methods: {
        ...mapMutations(['updateBalance', 'addTicket', 'setSelectionAdded', 'setMessage']),

        setChoice(choice) {
            this.choice = choice;
        },

        useStatSymbols(ids) {
            this.usingSymbols = ids;
        },
        setPurchaseAlert(res) {
            clearTimeout(this.alertTimeout);

            this.alertPayload = res;

            this.alertTimeout = setTimeout(() => {
                this.alertPayload = null;
                this.$nextTick(() => syncFrameHeight());
            }, 3000);

            this.$nextTick(() => syncFrameHeight());
        },
        handlePlayerText(res) {
          if (res.info == 'player_text') {
            this.setMessage({
              text: res.text,
              callback: () => {
                if (res.action === 'reload') {
                  location.reload();
                }
              },
            });
          }
        },
        async purchase() {
            this.isPurchasing = true;
            
            try {
                const { data: res } = await Axios.post(Config.route.purchase, {
                    mode: Config.mode,
                    shop_id: Config.shop_id,
                    draws_ids: this.nextDrawsIds,
                    game_id: this.purchaseGameId,
                    amount_id: this.selection.amountId,
                    count_id: this.selection.countId,
                    continue_count: this.selection.continueCount,
                    combination_id: this.selection.combinationId,
                    items: this.selection.gamesItems,
                    choice: this.game.name == 'Dice' ? undefined : this.choice,
                });

                this.handlePlayerText(res);

                if (res.success) {
                    this.updateBalance(res.balance);
                    this.setSelectionAdded(true);

                    for (const ticket of res.tickets) {
                        this.addTicket({ type: 'active', ticket, 'action': 'prepend' });
                    }

                    if (window.tv_purchase_telemetry) {
                        window.tv_purchase_telemetry(res.tickets);
                    }

                    parent && parent.postMessage({ balance: true }, '*');
                }

                this.setPurchaseAlert(res);

            } catch (err) {
                console.error(err);
                this.setPurchaseAlert({ text: Config.lang.error });
            }

            this.isPurchasing = false;
        },
    },

    watch: {
        purchaseGameId() {
            this.usingSymbols = [];
        },

        isFunctioning(isFunctional) {
          if (!isFunctional) {
            if (window.self != window.top) {
              parent.postMessage({ reload: true });
            } else {
              location.reload();
            }
          }
        },
    },
}
</script>
